/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import type { FeaturesContainer } from '@elara/client-core/containers/FeaturesContainer';
import type { AuthContainer } from '@elara/client-core/containers/FirebaseAuthContainer';
import type { FirestoreContainer } from '@elara/client-core/containers/FirestoreContainer';
import type { StorageContainer } from '@elara/client-core/containers/StorageContainer';
import type { FilePicker } from '@elara/client-core/lib/file-picker';
import type { ImageProcessor } from '@elara/client-core/lib/image-processor';
import type { KeyValueStore } from '@elara/client-core/lib/key-value-store';
import type { LifecyclePort } from '@elara/client-core/lib/lifecycle-port';
import type { SessionCookiePort } from '@elara/client-core/lib/session-cookie-port';
import type { SpeechRecognitionPort } from '@elara/client-core/lib/speech-recognition-port';
import type { ViewportPort } from '@elara/client-core/lib/viewport-port';

import type { AppSettings } from '#AppSettings.js';

import { getApp } from 'firebase/app';
import { getId, getInstallations } from 'firebase/installations';

import { createInstallIdProvider } from '@elara/client-core/lib/install-id';
import { startMonitors, stopMonitors } from '@elara/client-core/lib/monitorable';
import { createAnalyticsState } from '@elara/client-core/states/AnalyticsState';
import { createAuthFormState } from '@elara/client-core/states/AuthFormState';
import { createChatDebugState } from '@elara/client-core/states/ChatDebugState';
import { createChatInputStateManager } from '@elara/client-core/states/ChatInputStateManager';
import { createChatPresenceState } from '@elara/client-core/states/ChatPresenceState';
import { createChatState } from '@elara/client-core/states/ChatsState';
import { createDestinationGuideState } from '@elara/client-core/states/DestinationGuideState';
import { createDestinationSearchState } from '@elara/client-core/states/DestinationSearchState';
import { createFileUploadState } from '@elara/client-core/states/FileUploadState';
import { createFlightBookingOptionsState } from '@elara/client-core/states/FlightBookingOptionsState';
import { createFlightCheckoutState } from '@elara/client-core/states/FlightCheckoutState';
import { createHotelCheckoutState } from '@elara/client-core/states/HotelCheckoutState';
import { createHotelGuideState } from '@elara/client-core/states/HotelGuideState';
import { createHotelRoomSearchState } from '@elara/client-core/states/HotelRoomSearchState';
import { createInviteState } from '@elara/client-core/states/InviteState';
import { createItineraryState } from '@elara/client-core/states/ItineraryState';
import { createLifecycleState } from '@elara/client-core/states/LifecycleState';
import { createMembershipPaymentState } from '@elara/client-core/states/MembershipPaymentState';
import { createModelState } from '@elara/client-core/states/ModelState';
import { createOnboardingState } from '@elara/client-core/states/OnboardingState';
import { createPaymentMethodsState } from '@elara/client-core/states/PaymentMethodsState';
import { createPhoneLinkState } from '@elara/client-core/states/PhoneLinkState';
import { createPhoneRequirementState } from '@elara/client-core/states/PhoneRequirementState';
import { createPlaceGuideState } from '@elara/client-core/states/PlaceGuideState';
import { createPolicyAcceptanceState } from '@elara/client-core/states/PolicyAcceptanceState';
import { createProductTourState } from '@elara/client-core/states/ProductTourState';
import { createSessionState } from '@elara/client-core/states/SessionState';
import { createSharedItineraryState } from '@elara/client-core/states/SharedItineraryState';
import { createShortlistState } from '@elara/client-core/states/ShortlistState';
import { createShortlistUIState } from '@elara/client-core/states/ShortlistUIState';
import { createStoriesState } from '@elara/client-core/states/StoriesState';
import { createToastState } from '@elara/client-core/states/ToastState';
import { createTripsState } from '@elara/client-core/states/TripsState';
import { createUserProfileState } from '@elara/client-core/states/UserProfileState';
import { createContainer } from '@elara/core/di';

import { createAppState } from '#AppState.js';
import { createWebExternalUrlPort } from '#containers/external-url.web.js';
import { createWebPhoneVerificationPort } from '#containers/phone-verification.web.js';
import { createWebSocialAuthPort } from '#containers/social-auth.web.js';
import { createWebAttentionPort } from '#lib/web-attention-port.js';
import { createDebugInspectorState } from '#views/chat/DebugInspectorState.js';

import { createMessageViewState } from './MessageViewState.js';
import { createNotificationsState } from './NotificationsState.js';
import { createRouterState } from './RouterState.js';
import { createUiState } from './UIState.js';
import { createVersionState } from './VersionState.js';

export function createAppStatesContainer({
  features,
  auth,
  storage,
  firestore,
  settings,
  keyValueStore,
  lifecyclePort,
  viewport,
  speechRecognition,
  sessionCookie,
  filePicker,
  imageProcessor,
}: {
  features: FeaturesContainer;
  auth: AuthContainer;
  storage: StorageContainer;
  firestore: FirestoreContainer;
  settings: AppSettings;
  /** Platform persistence port (ELARA-691): web wraps localStorage, RN wraps MMKV. */
  keyValueStore: KeyValueStore;
  /** Platform app-lifecycle/connectivity port (ELARA-695): web wraps visibilitychange/online, RN wraps AppState/NetInfo. */
  lifecyclePort: LifecyclePort;
  /** Platform viewport-size port (ELARA-754): web reads window.innerWidth/resize, RN reads Dimensions. */
  viewport: ViewportPort;
  /** Platform speech-recognition port (ELARA-754): web wraps the Web Speech API, RN no-ops/native. */
  speechRecognition: SpeechRecognitionPort;
  /** Platform session-cookie port (ELARA-754): web mirrors the auth token into the __session cookie, RN no-ops. */
  sessionCookie: SessionCookiePort;
  /** Platform file selection port (ELARA-692): web wraps <input type=file>, RN wraps expo-image-picker. */
  filePicker: FilePicker;
  /** Platform image processing port (ELARA-692): web wraps canvas, RN wraps expo-image-manipulator. */
  imageProcessor: ImageProcessor;
}) {
  const $states = createContainer('app-states')
    .providedLazy('chatDebugState', () => createChatDebugState())
    .providedLazy('sessionState', () =>
      createSessionState(
        auth,
        sessionCookie,
        () => features.flows.warmup(),
        () => features.debugFlows.warmup(),
        createWebSocialAuthPort(auth),
      ),
    )
    .providedLazy('inviteState', () => createInviteState(keyValueStore))
    .dependent('sharedItineraryState', ['sessionState'], ({ sessionState }) => createSharedItineraryState(sessionState))
    .providedLazy('routerState', () => createRouterState(settings))
    .providedLazy('fileUploadState', () => createFileUploadState(storage))
    .providedLazy('appState', () => createAppState(settings))
    .providedLazy('toastState', () => createToastState())
    .providedLazy('uiState', () => createUiState())
    .providedLazy('modelState', () => createModelState(features.flows, keyValueStore))
    .dependent('userProfileState', ['sessionState', 'fileUploadState'], ({ sessionState, fileUploadState }) => {
      return createUserProfileState(sessionState, features.userProfile, fileUploadState);
    })
    .dependent('authFormState', ['sessionState'], ({ sessionState }) => {
      return createAuthFormState(sessionState);
    })
    .dependent('phoneLinkState', ['sessionState'], ({ sessionState }) => {
      return createPhoneLinkState(
        createWebPhoneVerificationPort(auth),
        sessionState,
        features.flows,
        createWebExternalUrlPort(),
      );
    })
    .dependent('analyticsState', ['routerState'], ({ routerState }) => {
      return createAnalyticsState(routerState);
    })
    .dependent('paymentMethodsState', ['sessionState'], ({ sessionState }) => {
      return createPaymentMethodsState(sessionState, features.wallet);
    })
    .dependent('membershipPaymentState', ['sessionState', 'userProfileState'], ({ sessionState, userProfileState }) => {
      return createMembershipPaymentState(features.flows, sessionState, userProfileState);
    })
    .dependent(
      'tripsState',
      ['sessionState', 'routerState', 'toastState'],
      ({ sessionState, routerState, toastState }) => {
        return createTripsState(
          sessionState,
          features.trips,
          routerState,
          toastState,
          keyValueStore,
          viewport,
          features.roomOptions,
        );
      },
    )
    .dependent(
      'onboardingState',
      ['sessionState', 'userProfileState', 'inviteState'],
      ({ sessionState, userProfileState, inviteState }) => {
        return createOnboardingState(
          sessionState,
          userProfileState,
          features.userProfile,
          features.flows,
          inviteState,
          keyValueStore,
        );
      },
    )
    .dependent('policyAcceptanceState', ['sessionState', 'userProfileState'], ({ sessionState, userProfileState }) => {
      return createPolicyAcceptanceState(sessionState, userProfileState, features.flows);
    })
    .dependent(
      'phoneRequirementState',
      ['phoneLinkState', 'sessionState', 'userProfileState', 'policyAcceptanceState', 'membershipPaymentState'],
      (deps) => createPhoneRequirementState(deps),
    )
    .dependent(
      'productTourState',
      ['userProfileState', 'tripsState', 'routerState', 'sessionState'],
      ({ userProfileState, tripsState, routerState, sessionState }) => {
        return createProductTourState(
          userProfileState,
          tripsState,
          routerState,
          sessionState,
          features.userProfile,
          features.flows,
        );
      },
    )
    .dependent(
      'chatsState',
      ['sessionState', 'tripsState', 'routerState', 'toastState', 'userProfileState'],
      ({ sessionState, tripsState, routerState, toastState, userProfileState }) => {
        return createChatState(
          sessionState,
          features.chats,
          routerState,
          toastState,
          tripsState,
          userProfileState,
          keyValueStore,
        );
      },
    )
    // The hotel guide's Rooms tab (ELARA-1092): reads the current trip and chat (for the concierge's brief).
    .dependent('hotelRoomSearchState', ['tripsState', 'chatsState'], ({ tripsState, chatsState }) =>
      createHotelRoomSearchState(tripsState, chatsState, features.flows, features.roomOptions),
    )
    .dependent('messageViewState', ['chatsState', 'toastState'], ({ chatsState, toastState }) =>
      createMessageViewState(chatsState, features.flows, toastState),
    )
    .dependent('shortlistState', ['tripsState', 'toastState'], ({ tripsState, toastState }) => {
      return createShortlistState(tripsState, features.shortlist, toastState);
    })
    .providedLazy('shortlistUIState', () => createShortlistUIState())
    .dependent('itineraryState', ['tripsState', 'toastState'], ({ tripsState, toastState }) => {
      return createItineraryState(
        tripsState,
        features.flows,
        features.destinations,
        features.hotels,
        features.itinerary,
        firestore,
        toastState,
      );
    })
    .dependent('destinationGuideState', [], () => {
      return createDestinationGuideState(features.destinations, features.flows);
    })
    .dependent('destinationSearchState', [], () => {
      return createDestinationSearchState(features.destinations);
    })
    .dependent('hotelGuideState', [], () => {
      return createHotelGuideState(features.hotels);
    })
    .dependent('hotelCheckoutState', [], () => {
      return createHotelCheckoutState(features.flows, features.hotels, features.roomOptions, features.itinerary);
    })
    .dependent('flightCheckoutState', [], () => {
      return createFlightCheckoutState(features.flows, features.flightOptions, features.itinerary);
    })
    .dependent('placeGuideState', [], () => {
      return createPlaceGuideState(features.places);
    })
    .providedLazy('storiesState', () =>
      createStoriesState(features.stories, features.hotels, features.destinations, features.places),
    )
    .dependent('flightBookingOptionsState', [], () => {
      return createFlightBookingOptionsState(features.flows);
    })
    .dependent(
      'chatInputState',
      ['chatsState', 'fileUploadState', 'messageViewState', 'tripsState', 'modelState'],
      ({ chatsState, fileUploadState, messageViewState, tripsState, modelState }) => {
        return createChatInputStateManager(
          chatsState,
          messageViewState,
          fileUploadState,
          tripsState,
          modelState,
          keyValueStore,
          filePicker,
          imageProcessor,
          speechRecognition,
        );
      },
    )
    .dependent('debugInspectorState', ['chatDebugState', 'chatsState'], ({ chatDebugState, chatsState }) => {
      return createDebugInspectorState(features.debugFlows, chatsState, chatDebugState);
    })
    // Which chat this tab is showing: reported to the server while a reply generates, so the reply's
    // push is skipped on every device, and checked locally for foreground pushes.
    .dependent(
      'chatPresenceState',
      ['routerState', 'tripsState', 'chatsState', 'sessionState'],
      ({ routerState, tripsState, chatsState, sessionState }) => {
        return createChatPresenceState({
          routerState,
          tripsState,
          chatsState,
          sessionState,
          attention: createWebAttentionPort(),
          isChatOnScreen: () => tripsState.isChatShown,
          report: async (input, options) => features.flows.reportChatPresence(input, options),
          getInstallId: createInstallIdProvider(keyValueStore, async () => getId(getInstallations(getApp()))),
        });
      },
    )
    .dependent('notificationsState', ['sessionState', 'routerState'], ({ sessionState, routerState }) => {
      return createNotificationsState(sessionState, features.notifications, features.flows, (path) =>
        routerState.navigate(path),
      );
    })
    .dependent('versionState', ['toastState'], ({ toastState }) => {
      return createVersionState(toastState);
    })
    .dependent('lifecycleState', ['sessionState'], ({ sessionState }) => {
      return createLifecycleState(lifecyclePort, sessionState);
    })
    .procedure(
      'startMonitors',
      [
        'sessionState',
        'userProfileState',
        'routerState',
        'tripsState',
        'chatsState',
        'chatInputState',
        'versionState',
        'notificationsState',
        'shortlistState',
        'itineraryState',
        'lifecycleState',
        'analyticsState',
        'chatPresenceState',
      ],
      startMonitors,
    )
    .procedure(
      'stopMonitors',
      [
        'sessionState',
        'userProfileState',
        'routerState',
        'tripsState',
        'chatsState',
        'chatInputState',
        'versionState',
        'notificationsState',
        'shortlistState',
        'itineraryState',
        'lifecycleState',
        'analyticsState',
        'chatPresenceState',
      ],
      stopMonitors,
    );
  return $states;
}

export type $AppStatesContainer = ReturnType<typeof createAppStatesContainer>;
export type AppStatesContainer = $AppStatesContainer['use'];
